/** @jsxImportSource @emotion/react */
import { ReactNode, useRef, useState } from 'react'

const MIN_LEFT = 360
const MIN_RIGHT = 480
const HANDLE = 16
const GRIP = 48
const KEY_STEP = 40

const readWidth = (storageKey: string): number | null => {
  try {
    const stored = Number(window.localStorage.getItem(storageKey))
    return stored > 0 ? stored : null
  } catch {
    return null
  }
}

const writeWidth = (storageKey: string, width: number | null) => {
  try {
    if (width === null) window.localStorage.removeItem(storageKey)
    else window.localStorage.setItem(storageKey, String(Math.round(width)))
  } catch {
    // Storage is a convenience; the split still works without it.
  }
}

interface Props {
  children: [ReactNode, ReactNode]
  // Until the user drags, the left pane takes half the width up to this cap
  // and the right pane gets everything beyond it.
  maxLeftWidth: number
  storageKey: string
}

/**
 * Two panes side by side with a draggable handle between them. The dragged
 * width is remembered per browser; double-clicking the handle resets it.
 */
export const ResizableSplit = ({ children: [left, right], maxLeftWidth, storageKey }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const leftRef = useRef<HTMLDivElement>(null)
  const [leftWidth, setLeftWidth] = useState(() => readWidth(storageKey))

  const clampWidth = (width: number) => {
    const total = containerRef.current?.getBoundingClientRect().width ?? 0
    return Math.max(MIN_LEFT, Math.min(width, total - HANDLE - MIN_RIGHT))
  }

  const resize = (width: number) => {
    const next = clampWidth(width)
    setLeftWidth(next)
    return next
  }

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const container = containerRef.current
    if (!container || event.button !== 0) return
    event.preventDefault()
    const handle = event.currentTarget
    handle.setPointerCapture(event.pointerId)
    const originX = container.getBoundingClientRect().left
    let latest = leftRef.current?.getBoundingClientRect().width ?? null

    const onMove = (move: PointerEvent) => {
      latest = resize(move.clientX - originX - HANDLE / 2)
    }
    const onUp = () => {
      handle.removeEventListener('pointermove', onMove)
      handle.removeEventListener('pointerup', onUp)
      handle.removeEventListener('pointercancel', onUp)
      document.body.style.removeProperty('user-select')
      writeWidth(storageKey, latest)
    }
    document.body.style.userSelect = 'none'
    handle.addEventListener('pointermove', onMove)
    handle.addEventListener('pointerup', onUp)
    handle.addEventListener('pointercancel', onUp)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === 'ArrowLeft' ? -KEY_STEP : event.key === 'ArrowRight' ? KEY_STEP : 0
    if (!step) return
    event.preventDefault()
    const current = leftRef.current?.getBoundingClientRect().width ?? MIN_LEFT
    writeWidth(storageKey, resize(current + step))
  }

  const reset = () => {
    setLeftWidth(null)
    writeWidth(storageKey, null)
  }

  return (
    <div ref={containerRef} css={{ display: 'flex', alignItems: 'flex-start' }}>
      <div
        ref={leftRef}
        css={
          leftWidth === null
            ? { flex: `0 1 calc(50% - ${HANDLE / 2}px)`, maxWidth: maxLeftWidth, minWidth: 0 }
            : {
                flex: '0 0 auto',
                width: leftWidth,
                // A width saved on a wider screen still leaves the right pane room.
                maxWidth: `calc(100% - ${HANDLE + MIN_RIGHT}px)`,
              }
        }
      >
        {left}
      </div>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panels"
        title="Drag to resize · double-click to reset"
        tabIndex={0}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
        onDoubleClick={reset}
        css={{
          flex: `0 0 ${HANDLE}px`,
          alignSelf: 'stretch',
          cursor: 'col-resize',
          touchAction: 'none',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          outline: 'none',
          // The whole seam drags; the grip holds the middle of the window as
          // the page scrolls, staying within the seam.
          '&::before': {
            content: '""',
            position: 'sticky',
            top: `calc(50vh - ${GRIP / 2}px)`,
            bottom: `calc(50vh - ${GRIP / 2}px)`,
            width: 4,
            height: GRIP,
            borderRadius: 2,
            background: '#dee2e6',
            transition: 'background-color 0.15s',
          },
          '&:hover::before, &:focus-visible::before, &:active::before': {
            background: '#4a89b3',
          },
        }}
      />
      <div css={{ flex: '1 1 0', minWidth: 0 }}>{right}</div>
    </div>
  )
}
